import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createAgeFilterApi,
  createCustomFilterApi,
  deleteAgeFilterApi,
  deleteCustomFilterApi,
  getAgeFilterApi,
  getAgeFiltersApi,
  getCustomFilterApi,
  getCustomFiltersApi,
  updateAgeFilterApi,
  updateCustomFilterApi,
} from "../../api/ecommerce/ProductFilterApi";

const initialState = {
  customFilter: null,
  customFilters: [],
  ageFilter: null,
  ageFilters: [],
  createResponse: null,
  updateResponse: null,
  deleteResponse: null,
  loading: false,
  detailsLoading: false,
  submitting: false,
  deleting: false,
  success: false,
};

export const getCustomFilter = createAsyncThunk(
  "productFilter/getCustomFilter",
  async (id, { rejectWithValue }) => {
    try {
      const response = await getCustomFilterApi(id);
      return response?.data;
    } catch (error) {
      return rejectWithValue({
        errorMessage: error?.data?.message || error?.message,
      });
    }
  }
);

export const getCustomFilters = createAsyncThunk(
  "productFilter/getCustomFilters",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getCustomFiltersApi();
      return response?.data;
    } catch (error) {
      return rejectWithValue({
        errorMessage: error?.data?.message || error?.message,
      });
    }
  }
);

export const createCustomFilter = createAsyncThunk(
  "productFilter/createCustomFilter",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createCustomFilterApi(payload);
      return response?.data;
    } catch (error) {
      return rejectWithValue({
        errorMessage: error?.data?.message || error?.message,
      });
    }
  }
);

export const updateCustomFilter = createAsyncThunk(
  "productFilter/updateCustomFilter",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateCustomFilterApi(payload);
      return response?.data;
    } catch (error) {
      return rejectWithValue({
        errorMessage: error?.data?.message || error?.message,
      });
    }
  }
);

export const deleteCustomFilter = createAsyncThunk(
  "productFilter/deleteCustomFilter",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteCustomFilterApi(id);
      return response?.data;
    } catch (error) {
      return rejectWithValue({
        errorMessage: error?.data?.message || error?.message,
      });
    }
  }
);

export const getAgeFilter = createAsyncThunk(
  "productFilter/getAgeFilter",
  async (id, { rejectWithValue }) => {
    try {
      const response = await getAgeFilterApi(id);
      return response?.data;
    } catch (error) {
      return rejectWithValue({
        errorMessage: error?.data?.message || error?.message,
      });
    }
  }
);

export const getAgeFilters = createAsyncThunk(
  "productFilter/getAgeFilters",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAgeFiltersApi();
      return response?.data;
    } catch (error) {
      return rejectWithValue({
        errorMessage: error?.data?.message || error?.message,
      });
    }
  }
);

export const createAgeFilter = createAsyncThunk(
  "productFilter/createAgeFilter",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createAgeFilterApi(payload);
      return response?.data;
    } catch (error) {
      return rejectWithValue({
        errorMessage: error?.data?.message || error?.message,
      });
    }
  }
);

export const updateAgeFilter = createAsyncThunk(
  "productFilter/updateAgeFilter",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateAgeFilterApi(payload);
      return response?.data;
    } catch (error) {
      return rejectWithValue({
        errorMessage: error?.data?.message || error?.message,
      });
    }
  }
);

export const deleteAgeFilter = createAsyncThunk(
  "productFilter/deleteAgeFilter",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteAgeFilterApi(id);
      return response?.data;
    } catch (error) {
      return rejectWithValue({
        errorMessage: error?.data?.message || error?.message,
      });
    }
  }
);

const productFilterSlice = createSlice({
  name: "productFilter",
  initialState,
  reducers: {
    clearProductFilterState: (state) => {
      state.createResponse = null;
      state.updateResponse = null;
      state.deleteResponse = null;
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCustomFilter.pending, (state) => {
      state.detailsLoading = true;
      state.success = false;
    });
    builder.addCase(getCustomFilter.fulfilled, (state, action) => {
      state.customFilter = action?.payload?.response ?? action?.payload;
      state.detailsLoading = false;
      state.success = true;
    });
    builder.addCase(getCustomFilter.rejected, (state) => {
      state.detailsLoading = false;
      state.success = false;
    });

    builder.addCase(getCustomFilters.pending, (state) => {
      state.loading = true;
      state.success = false;
    });
    builder.addCase(getCustomFilters.fulfilled, (state, action) => {
      state.customFilters = action?.payload?.response ?? action?.payload ?? [];
      state.loading = false;
      state.success = true;
    });
    builder.addCase(getCustomFilters.rejected, (state) => {
      state.loading = false;
      state.success = false;
    });

    builder.addCase(createCustomFilter.pending, (state) => {
      state.submitting = true;
      state.success = false;
    });
    builder.addCase(createCustomFilter.fulfilled, (state, action) => {
      state.createResponse = action?.payload?.response ?? action?.payload;
      state.customFilter = action?.payload?.response ?? action?.payload;
      state.submitting = false;
      state.success = true;
    });
    builder.addCase(createCustomFilter.rejected, (state) => {
      state.submitting = false;
      state.success = false;
    });

    builder.addCase(updateCustomFilter.pending, (state) => {
      state.submitting = true;
      state.success = false;
    });
    builder.addCase(updateCustomFilter.fulfilled, (state, action) => {
      state.updateResponse = action?.payload?.response ?? action?.payload;
      state.customFilter = action?.payload?.response ?? action?.payload;
      state.submitting = false;
      state.success = true;
    });
    builder.addCase(updateCustomFilter.rejected, (state) => {
      state.submitting = false;
      state.success = false;
    });

    builder.addCase(deleteCustomFilter.pending, (state) => {
      state.deleting = true;
      state.success = false;
    });
    builder.addCase(deleteCustomFilter.fulfilled, (state, action) => {
      state.deleteResponse = action?.payload?.response ?? action?.payload;
      state.deleting = false;
      state.success = true;
    });
    builder.addCase(deleteCustomFilter.rejected, (state) => {
      state.deleting = false;
      state.success = false;
    });

    builder.addCase(getAgeFilter.pending, (state) => {
      state.detailsLoading = true;
      state.success = false;
    });
    builder.addCase(getAgeFilter.fulfilled, (state, action) => {
      state.ageFilter = action?.payload?.response ?? action?.payload;
      state.detailsLoading = false;
      state.success = true;
    });
    builder.addCase(getAgeFilter.rejected, (state) => {
      state.detailsLoading = false;
      state.success = false;
    });

    builder.addCase(getAgeFilters.pending, (state) => {
      state.loading = true;
      state.success = false;
    });
    builder.addCase(getAgeFilters.fulfilled, (state, action) => {
      state.ageFilters = action?.payload?.response ?? action?.payload ?? [];
      state.loading = false;
      state.success = true;
    });
    builder.addCase(getAgeFilters.rejected, (state) => {
      state.loading = false;
      state.success = false;
    });

    builder.addCase(createAgeFilter.pending, (state) => {
      state.submitting = true;
      state.success = false;
    });
    builder.addCase(createAgeFilter.fulfilled, (state, action) => {
      state.createResponse = action?.payload?.response ?? action?.payload;
      state.ageFilter = action?.payload?.response ?? action?.payload;
      state.submitting = false;
      state.success = true;
    });
    builder.addCase(createAgeFilter.rejected, (state) => {
      state.submitting = false;
      state.success = false;
    });

    builder.addCase(updateAgeFilter.pending, (state) => {
      state.submitting = true;
      state.success = false;
    });
    builder.addCase(updateAgeFilter.fulfilled, (state, action) => {
      state.updateResponse = action?.payload?.response ?? action?.payload;
      state.ageFilter = action?.payload?.response ?? action?.payload;
      state.submitting = false;
      state.success = true;
    });
    builder.addCase(updateAgeFilter.rejected, (state) => {
      state.submitting = false;
      state.success = false;
    });

    builder.addCase(deleteAgeFilter.pending, (state) => {
      state.deleting = true;
      state.success = false;
    });
    builder.addCase(deleteAgeFilter.fulfilled, (state, action) => {
      state.deleteResponse = action?.payload?.response ?? action?.payload;
      state.deleting = false;
      state.success = true;
    });
    builder.addCase(deleteAgeFilter.rejected, (state) => {
      state.deleting = false;
      state.success = false;
    });
  },
});

export const { clearProductFilterState } = productFilterSlice.actions;
export default productFilterSlice.reducer;
